import { supabase } from "@/lib/supabase";
import { USE_SUPABASE } from "@/lib/dataSource";
import { PrestataireSchema, ContactSchema, type PrestataireRow, type PrestataireUpdate, type ContactRow } from "@/schemas/prestataire";
import { MOCK_PRESTATAIRES } from "@/data/mock";
import { z } from "zod";

/**
 * Repository des prestataires.
 *
 * Les `contacts` d'un prestataire sont stockés dans une table séparée
 * (`contacts` avec FK `prestataire_id`). Le repository les hydrate à la
 * demande via `getContacts(role, prestataireId)`.
 *
 * Les métiers sont stockés dans la table de jonction `prestataires_metiers` —
 * hydratation future (Phase D) via un helper `listMetiersForPrestataire`.
 */

function fromMocks(): PrestataireRow[] {
  return MOCK_PRESTATAIRES as unknown as PrestataireRow[];
}

async function listFromSupabase(role: string): Promise<PrestataireRow[]> {
  const { data, error } = await supabase.rpc("get_prestataires_for_role", { p_role: role });
  if (error) throw new Error(`listPrestataires: ${error.message}`);
  return z.array(PrestataireSchema).parse(data ?? []);
}

async function getFromSupabase(role: string, id: string): Promise<PrestataireRow | null> {
  const { data, error } = await supabase.rpc("get_prestataire_for_role", { p_role: role, p_id: id });
  if (error) throw new Error(`getPrestataire(${id}): ${error.message}`);
  if (!data) return null;
  return PrestataireSchema.parse(data);
}

async function updateOnSupabase(role: string, id: string, patch: PrestataireUpdate): Promise<PrestataireRow> {
  const { data, error } = await supabase.rpc("update_entity_with_perms", {
    p_table_name: "prestataires",
    p_role: role,
    p_id: id,
    p_data: patch,
  });
  if (error) throw new Error(`updatePrestataire(${id}): ${error.message}`);
  return PrestataireSchema.parse(data);
}

async function getContactsFromSupabase(role: string, prestataireId: string): Promise<ContactRow[]> {
  const { data, error } = await supabase.rpc("get_contacts_for_prestataire", {
    p_role: role,
    p_prestataire_id: prestataireId,
  });
  if (error) throw new Error(`getContacts(${prestataireId}): ${error.message}`);
  return z.array(ContactSchema).parse(data ?? []);
}

export const prestatairesRepository = {
  async list(role: string): Promise<PrestataireRow[]> {
    if (!USE_SUPABASE) return fromMocks();
    return listFromSupabase(role);
  },

  async get(role: string, id: string): Promise<PrestataireRow | null> {
    if (!USE_SUPABASE) {
      return (fromMocks().find((p) => p.id === id) ?? null);
    }
    return getFromSupabase(role, id);
  },

  async update(role: string, id: string, patch: PrestataireUpdate): Promise<PrestataireRow> {
    if (!USE_SUPABASE) {
      const p = fromMocks().find((x) => x.id === id);
      if (!p) throw new Error(`Prestataire introuvable: ${id}`);
      return { ...p, ...patch };
    }
    return updateOnSupabase(role, id, patch);
  },

  async getContacts(role: string, prestataireId: string): Promise<ContactRow[]> {
    if (!USE_SUPABASE) {
      const p = fromMocks().find((x) => x.id === prestataireId);
      return (p?.contacts ?? []) as ContactRow[];
    }
    return getContactsFromSupabase(role, prestataireId);
  },
};
